import "../../utils/viewportHeight";
import setupOffcanvasCartUI from "./setupOffcanvasCartUI";
import setupOffcanvasLoginUI from "./setupOffcanvasLoginUI";
import setupOffcanvasFavoritesUI from "./setupOffcanvasFavoritesUI";
import setupOffcanvasSearchUI from "./setupOffcanvasSearchUI";
import initializeLanguageSelector from "./langSelector";

// Variables
const overlay = document.querySelector(".overlay");
const header = document.querySelector(".main-header");
const mainMenuProductsItemAnchor = document.querySelector(
  ".main-navigation .products-item"
);
const narrowMenuProductsItemAnchor = document.querySelector(
  ".narrow-submenu .products-item"
);
const submenu = document.querySelector(".submenu");
const headerContainer = document.querySelector(".header-container");
const menuBtn = document.querySelector("#menu-toggle");
const narrowSubMenu = document.querySelector(".narrow-submenu");

// Only initialize if required header elements exist
if (header && overlay) {
  const observer = new MutationObserver(() => {
    toggleOverlay();
  });

  // Helper Functions
  function toggleOverlay() {
    if (narrowSubMenu.classList.contains("show")) {
      overlay.classList.remove("hide");
      overlay.classList.add("show");
    } else {
      overlay.classList.remove("show");
      overlay.classList.add("hide");
    }
  }

  // Global submenu functions
  function showSubmenu() {
    submenu.classList.remove("hide");
    submenu.classList.add("show");
  }

  function hideSubmenu() {
    submenu.classList.remove("show");
    submenu.classList.add("hide");
  }

  // Desktop submenu functionality
  if (mainMenuProductsItemAnchor && submenu && headerContainer) {
    function handleHoverSubmenu() {
      showSubmenu();
      mainMenuProductsItemAnchor
        .querySelector("a")
        .classList.add("nav-link-active");
    }

    function handleMouseOutSubmenu() {
      if (
        !mainMenuProductsItemAnchor.matches(":hover") &&
        !submenu.matches(":hover")
      ) {
        hideSubmenu();
        mainMenuProductsItemAnchor
          .querySelector("a")
          .classList.remove("nav-link-active");
      }
    }

    // Hover events for desktop menu
    [mainMenuProductsItemAnchor, submenu].forEach((item) => {
      item.addEventListener("mouseover", handleHoverSubmenu);
      item.addEventListener("mouseout", handleMouseOutSubmenu);
    });
  }

  // Function to display narrow menu
  if (menuBtn && narrowSubMenu) {
    function toggleNarrowSubmenu() {
      narrowSubMenu.classList.toggle("show");
      narrowSubMenu.classList.toggle("hide");
      submenu.classList.remove("show");
      submenu.classList.add("hide");
    }

    function checkViewportWidth() {
      if (window.innerWidth > 1024) {
        narrowSubMenu.classList.add("hide");
        narrowSubMenu.classList.remove("show");
      }
    }

    menuBtn.addEventListener("click", toggleNarrowSubmenu);
    window.addEventListener("resize", checkViewportWidth);

    // Initialize observer for mobile menu
    observer.observe(narrowSubMenu, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // Initial viewport check
    checkViewportWidth();
  }

  // Listener to display submenu from mobile view
  if (narrowMenuProductsItemAnchor) {
    narrowMenuProductsItemAnchor.addEventListener("click", showSubmenu);
  }

  // Global click handler for outside clicks
  function closeOnOutsideClick(event) {
    const isClickInsideMenu =
      header.contains(event.target) ||
      narrowSubMenu.contains(event.target) ||
      submenu.contains(event.target);

    if (!isClickInsideMenu) {
      narrowSubMenu.classList.remove("show");
      narrowSubMenu.classList.add("hide");

      submenu.classList.remove("show");
      submenu.classList.add("hide");
    }
  }

  document.addEventListener("click", closeOnOutsideClick);
}

// Tab menu functionality
function initializeTabMenu() {
  const tabHeaders = document.querySelectorAll(".tab-header");
  const tabPanels = document.querySelectorAll(".tab-panel");
  const showcaseSets = document.querySelectorAll(".showcase-set");

  // Load images for a specific category
  function loadImagesForCategory(category) {
    const set = document.querySelector(
      `.showcase-set[data-category="${category}"]`
    );
    if (!set) return;

    set.querySelectorAll("img[data-src]").forEach((img) => {
      if (!img.src) {
        img.src = img.dataset.src;
      }
    });
  }

  function switchTab(e) {
    const targetCategory = e.target.dataset.category;

    // Load images before showing them
    loadImagesForCategory(targetCategory);

    // Update tabs and panels
    tabHeaders.forEach((header) => {
      header.classList.toggle(
        "active",
        header.dataset.category === targetCategory
      );
    });

    tabPanels.forEach((panel) => {
      panel.classList.toggle(
        "active",
        panel.dataset.category === targetCategory
      );
    });

    // Update showcase images
    showcaseSets.forEach((set) => {
      const isActive = set.dataset.category === targetCategory;
      set.classList.toggle("active", isActive);

      if (isActive) {
        // Animate in images with delay
        const images = set.querySelectorAll(".img-wrapper");
        images.forEach((img, index) => {
          setTimeout(() => {
            img.classList.add("active");
          }, index * 100);
        });
      } else {
        // Remove active class from all images
        set.querySelectorAll(".img-wrapper").forEach((img) => {
          img.classList.remove("active");
        });
      }
    });
  }

  // Preload images for the next category
  function preloadNextCategory(currentCategory) {
    const categories = Array.from(tabHeaders).map(
      (header) => header.dataset.category
    );
    const currentIndex = categories.indexOf(currentCategory);
    const nextCategory = categories[(currentIndex + 1) % categories.length];

    if (nextCategory) {
      loadImagesForCategory(nextCategory);
    }
  }

  // Enhanced hover listener
  tabHeaders.forEach((header) => {
    header.addEventListener("mouseenter", (e) => {
      switchTab(e);
      // Preload next category's images
      preloadNextCategory(e.target.dataset.category);
    });
  });

  // Initialize first category only
  if (tabHeaders[0]) {
    const firstCategory = tabHeaders[0].dataset.category;
    tabHeaders[0].classList.add("active");

    // Initial setup for first category
    const firstShowcaseSet = document.querySelector(
      `.showcase-set[data-category="${firstCategory}"]`
    );
    if (firstShowcaseSet) {
      firstShowcaseSet.classList.add("active");
      firstShowcaseSet
        .querySelectorAll(".img-wrapper")
        .forEach((img, index) => {
          setTimeout(() => {
            img.classList.add("active");
          }, index * 100);
        });
    }

    // Preload the second category's images after a short delay
    setTimeout(() => {
      preloadNextCategory(firstCategory);
    }, 1000);
  }
}

// Cookie Notification
function initializeCookieNotification() {
  const cookieNotification = document.querySelector(".cookie-notification");
  if (!cookieNotification) return;

  const closeButton = cookieNotification.querySelector(".close-cookie");
  if (!closeButton) return;

  // Check if notification was previously dismissed
  const isDismissed = localStorage.getItem("isCookieNotificationDismissed");

  if (!isDismissed) {
    // Show notification after a short delay
    setTimeout(() => {
      cookieNotification.classList.add("show-cookie");
      cookieNotification.classList.remove("hide-cookie");
    }, 5000);
  } else {
    cookieNotification.classList.add("hide-cookie");
  }

  closeButton.addEventListener("click", () => {
    cookieNotification.classList.remove("show-cookie");
    cookieNotification.classList.add("hide-cookie");
    localStorage.setItem("isCookieNotificationDismissed", "true");
  });
}

// Discount Notification
function initializeDiscountNotification() {
  const discountNotification = document.querySelector(".discount-notification");
  if (!discountNotification) return;

  const closeButton = discountNotification.querySelector(".close-discount");
  if (!closeButton) return;

  // Check if notification was previously dismissed
  const isDismissed = sessionStorage.getItem("isDiscountNotificationDismissed");

  if (!isDismissed) {
    // Show notification after a 5 second delay
    setTimeout(() => {
      discountNotification.classList.add("show-discount");
      discountNotification.classList.remove("hide-discount");
    }, 3000);
  } else {
    discountNotification.classList.add("hide-discount");
  }

  closeButton.addEventListener("click", () => {
    discountNotification.classList.remove("show-discount");
    discountNotification.classList.add("hide-discount");
    sessionStorage.setItem("isDiscountNotificationDismissed", "true");
  });
}

// Single initialization
document.addEventListener("DOMContentLoaded", () => {
  initializeTabMenu();
  setupOffcanvasCartUI();
  setupOffcanvasLoginUI();
  setupOffcanvasFavoritesUI();
  setupOffcanvasSearchUI();
  initializeLanguageSelector();
  initializeCookieNotification();
  initializeDiscountNotification();
});
